import { FC, FormEvent, useEffect, useLayoutEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { mapValues } from 'lodash';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  DateFilters,
  Dropdown,
  facet,
  FeatureFlags,
  Icon,
  MultiselectDropdownItem,
  SearchOption,
  stringifyFacetsForUrl,
  stringifyPublicationYear,
} from '@app/components';
import { useSearchContext } from '@app/contexts';
import { DateRange, generateRouteParams, removeEmptyKeys } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { defaultRecordsPerPage, Feature, Route } from '@app/constants';

import { SearchRow } from './SearchRow';
import { LimiterDropdown } from './LimiterDropdown';
import {
  checkObjectStateSelection,
  createMaskedAdvancedSearchQuery,
  getFormValuesObject,
  parseMaskedAdvancedSearchQuery,
  useAdvancedSearchLimiters,
} from './utils';
import {
  advancedSearchFormRowCount,
  BooleanChip,
  defaultAdvancedSearchRowState,
  defaultUnmappedLimitersValue,
  FormValues,
  limitersDefaultValue,
  maxRowCount,
  RowValues,
  SearchLimiterKeys,
} from './constants';

import './AdvancedSearchForm.scss';

const cnBem = createBemBlockBuilder(['advancedSearchForm']);

export const AdvancedSearchForm: FC = () => {
  const [values, setValues] = useState<FormValues>(getFormValuesObject());
  const { $t } = useIntl();
  const {
    state,
    shouldRefocusRef,
    setSelectedPublicationYear,
    selectedPublicationYear,
    setSearchOption,
    updateState,
  } = useSearchContext();
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({});

  const {
    searchLimiters,
    sourceTypeItems,
    languageItems,
    locationsItems,
    unmappedLimiters,
    areSourceTypesLoading,
    areLanguagesLoading,
    areLocationsLoading,
    setSearchLimiters,
    setUnmappedLimiters,
  } = useAdvancedSearchLimiters();
  const navigate = useNavigate();
  const rowCount = Object.keys(values).length;
  const allOption = { label: $t(sharedMessages.all) };

  useEffect(() => {
    if (selectedPublicationYear) {
      setSelectedDateRange(selectedPublicationYear);
    }
  }, [selectedPublicationYear]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    shouldRefocusRef.current = true;

    const query = createMaskedAdvancedSearchQuery(values);
    const selectedLimitersValues = mapValues(
      searchLimiters,
      limiterOptions => limiterOptions?.map(option => option.id) ?? []
    );

    const areLimitersSelected = checkObjectStateSelection(
      selectedLimitersValues
    );
    const isDateRangeSelected = checkObjectStateSelection(selectedDateRange);
    const isNonEmptySearch =
      query || areLimitersSelected || isDateRangeSelected;

    setSearchOption(SearchOption.advanced);
    setSelectedPublicationYear(selectedDateRange);

    navigate(
      generateRouteParams({
        pathname: Route.search,
        query: {
          ...(isNonEmptySearch &&
            removeEmptyKeys({
              query,
              option: SearchOption.advanced,
              facets: stringifyFacetsForUrl(
                areLimitersSelected ? selectedLimitersValues : undefined
              ),
              publicationYear: stringifyPublicationYear(
                isDateRangeSelected ? selectedDateRange : undefined
              ),
              pageNumber: '1',
              recordsPerPage: `${defaultRecordsPerPage}`,
            })),
        },
      })
    );

    if (!isNonEmptySearch) {
      updateState({
        option: SearchOption.advanced,
      });
    }
  };

  const handleLimitersChange = (
    options: MultiselectDropdownItem[],
    limiterKey: SearchLimiterKeys
  ) => {
    setSearchLimiters({ [limiterKey]: options });
    setUnmappedLimiters(defaultUnmappedLimitersValue);
  };

  const updateRow = (rowIndex: number) => (updatedValues: RowValues) => {
    setValues(prevValues => ({
      ...prevValues,
      [rowIndex]: updatedValues,
    }));
  };

  const handleClearAll = () => {
    setValues(getFormValuesObject());
    setSelectedDateRange({});
    setSearchLimiters(limitersDefaultValue);
    setUnmappedLimiters({
      [facet.format]: null,
      [facet.language]: null,
      [facet.location]: null,
    });

    navigate(Route.advancedSearch);
  };

  useLayoutEffect(() => {
    const isAdvancedSearch =
      state.option === SearchOption.advanced && Boolean(state.query);

    if (
      isAdvancedSearch &&
      createMaskedAdvancedSearchQuery(values) !== state.query
    ) {
      const formValues = parseMaskedAdvancedSearchQuery(state.query);

      setValues(formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.query, state.option]);

  const dateRangeLabel = $t(sharedMessages.publicationYear);

  return (
    <form className={cnBem()} role="search" onSubmit={handleSubmit}>
      <div className={cnBem('__fields')}>
        {Object.values(values).map((data, index) => (
          <SearchRow
            {...data}
            index={index}
            totalRowsCount={advancedSearchFormRowCount}
            key={`row-${index}`}
            updateRow={updateRow(index)}
          />
        ))}
        {rowCount < maxRowCount && (
          <Button
            styleType="flat-icon"
            className={cnBem('__addFieldsButton')}
            onClick={() => {
              setValues(prevValues => ({
                ...prevValues,
                [rowCount]: {
                  ...defaultAdvancedSearchRowState,
                  booleanChip: BooleanChip.and,
                },
              }));
            }}
          >
            <Icon icon={faPlusCircle} />
            {$t({
              id: 'advancedSearch.addFields',
              defaultMessage: 'Add fields',
            })}
          </Button>
        )}
      </div>
      <div className={cnBem('__limiters')}>
        <h2>
          {$t({
            id: 'advancedSearch.searchLimiters',
            defaultMessage: 'Search limiters',
          })}
        </h2>
        <LimiterDropdown
          options={sourceTypeItems}
          initiallySelectedOptions={searchLimiters.sourceTypes}
          unmappedInitialLimiters={unmappedLimiters.sourceTypes}
          label={$t(sharedMessages.format)}
          isLoading={areSourceTypesLoading}
          onSelectionChange={options =>
            handleLimitersChange(options, facet.format)
          }
        />
        <LimiterDropdown
          options={languageItems}
          initiallySelectedOptions={searchLimiters.languages}
          unmappedInitialLimiters={unmappedLimiters.languages}
          label={$t(sharedMessages.language)}
          isLoading={areLanguagesLoading}
          onSelectionChange={options =>
            handleLimitersChange(options, facet.language)
          }
        />
        <FeatureFlags
          flagsToMatch={[{ name: Feature.LCAdvancedSearchPlaceOfPublication }]}
        >
          <Dropdown
            id="advanced-search-place-of-publication"
            label={$t({
              id: 'advancedSearch.placeOfPublication',
              defaultMessage: 'Place of publication',
            })}
            styleType="standard"
            items={[allOption]}
            selectedItem={allOption}
          />
        </FeatureFlags>
        <LimiterDropdown
          options={locationsItems}
          initiallySelectedOptions={searchLimiters[facet.location]}
          unmappedInitialLimiters={unmappedLimiters[facet.location]}
          label={$t(sharedMessages.location)}
          isLoading={areLocationsLoading}
          onSelectionChange={options =>
            handleLimitersChange(options, facet.location)
          }
        />
        <div className={cnBem('__dateRange')}>
          <label className="eb-dropdown__label eb-dropdown__label--standard">
            {dateRangeLabel}
          </label>
          <DateFilters
            hasApplyButton={false}
            selectedDateRange={selectedDateRange}
            ariaLabel={dateRangeLabel}
            onDateRangeChange={setSelectedDateRange}
          />
        </div>
      </div>
      <div className={cnBem('__footer')}>
        <div className={cnBem('__footer-content')}>
          <Button styleType="passive" onClick={handleClearAll}>
            {$t(sharedMessages.clearAll)}
          </Button>
          <Button type="submit" styleType="highlighted">
            {$t(sharedMessages.search)}
          </Button>
        </div>
      </div>
    </form>
  );
};
